// ManageHostelAttendanceReport.js - Hostel Student Attendance Search & Multi-Tier Hierarchical Report
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Typography,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  Autocomplete,
  Stack,
  Divider,
  TablePagination,
  InputAdornment,
  IconButton,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import PersonIcon from "@mui/icons-material/Person";
import EventNoteIcon from "@mui/icons-material/EventNote";
import DeleteIcon from "@mui/icons-material/Delete";

import { API_URL } from "./config";

const FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "SF Pro", "Helvetica Neue", Arial, sans-serif';

/**
 * Level 3: Nested Component for a Student's Date-Wise Attendance History with Pagination
 */
function StudentAttendanceDatesHistory({ studentId, studentName, campusCode, authHeader, THEME_ORANGE, fromDate, toDate }) {
  const [loading, setLoading] = useState(true);
  const [dateRows, setDateRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteRemark, setDeleteRemark] = useState("");
  const [deleteRemarkError, setDeleteRemarkError] = useState("");

  const loadRows = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${API_URL}/api/HostelAttendance/students/${encodeURIComponent(studentId)}/dates`,
        { ...authHeader, params: { CampusCode: campusCode, FromDate: fromDate || undefined, ToDate: toDate || undefined } }
      );
      setDateRows(Array.isArray(data) ? data : []);
      setSelectedIds(new Set());
      setPage(0);
    } catch (err) {
      console.error("Failed to load student attendance dates:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRows(); /* eslint-disable-next-line */ }, [studentId, campusCode, authHeader, fromDate, toDate]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return dateRows.slice(start, start + rowsPerPage);
  }, [dateRows, page, rowsPerPage]);

  const deletableIds = dateRows.map((r) => r.HostelAttendanceID).filter(Boolean);
  const pageIds = paginatedRows.map((r) => r.HostelAttendanceID).filter(Boolean);
  const selectedOnPage = pageIds.filter((id) => selectedIds.has(id));

  const toggleOne = (id) => {
    if (!id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const togglePage = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      pageIds.forEach((id) => checked ? next.add(id) : next.delete(id));
      return next;
    });
  };

  const requestDeleteSelected = () => {
    if (!selectedIds.size) return;
    setDeleteRemark("");
    setDeleteRemarkError("");
    setDeleteDialogOpen(true);
  };

  const deleteSelected = async () => {
    const ids = [...selectedIds];
    const remark = deleteRemark.trim();
    if (!ids.length) return;
    if (!remark) {
      setDeleteRemarkError("Remark is required before deleting attendance records.");
      return;
    }
    try {
      setDeleting(true);
      await axios.delete(`${API_URL}/api/HostelAttendance/delete`, {
        ...authHeader,
        data: { ids, remark },
      });
      setDeleteDialogOpen(false);
      setDeleteRemark("");
      setDeleteRemarkError("");
      await loadRows();
    } catch (err) {
      console.error(err);
      setDeleteRemarkError(err.response?.data?.error || "Failed to delete attendance record(s).");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ m: 1.5, p: 2, borderRadius: 2, backgroundColor: "#FFFFFF", border: "1px solid rgba(25,118,210,0.20)" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <EventNoteIcon sx={{ color: THEME_ORANGE, fontSize: "1.2rem" }} />
          <Typography sx={{ fontWeight: 900, fontSize: "0.92rem", color: THEME_ORANGE }}>
            Attendance Dates Log • {studentName} ({studentId})
          </Typography>
          <Chip size="small" label={`${dateRows.length} Sessions`} sx={{ fontWeight: 800 }} />
        </Box>
        <Button variant="contained" color="error" size="small" startIcon={<DeleteIcon />} disabled={!selectedIds.size || deleting} onClick={requestDeleteSelected} sx={{ textTransform: "none", fontWeight: 800 }}>
          {deleting ? "Deleting..." : `Delete Selected (${selectedIds.size})`}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2.5 }}><CircularProgress size={20} sx={{ color: THEME_ORANGE }} /><Typography>Loading attendance dates…</Typography></Box>
      ) : dateRows.length === 0 ? (
        <Typography sx={{ py: 2, color: "text.secondary" }}>No attendance sessions found for this student in the selected date range.</Typography>
      ) : (
        <>
          <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid rgba(25,118,210,0.18)", borderRadius: 1.5, overflow: "hidden" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "rgba(25,118,210,0.95)", "& th": { color: "#fff", fontWeight: 900 } }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={pageIds.length > 0 && selectedOnPage.length === pageIds.length}
                      indeterminate={selectedOnPage.length > 0 && selectedOnPage.length < pageIds.length}
                      onChange={(e) => togglePage(e.target.checked)}
                      sx={{ color: "rgba(255,255,255,.75)", "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#fff" } }}
                    />
                  </TableCell>
                  <TableCell>#</TableCell><TableCell>Attendance Date</TableCell><TableCell>Block</TableCell><TableCell>Floor</TableCell><TableCell>Room</TableCell><TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row, idx) => {
                  const isPresent = row.Status === "Present" || row.Status === 1 || row.Status === "1";
                  const id = row.HostelAttendanceID;
                  return <TableRow key={`${row.SessionID}-${row.AttendanceDate}-${idx}`} sx={{ backgroundColor: idx % 2 === 0 ? "rgba(25,118,210,0.06)" : "#fff" }}>
                    <TableCell padding="checkbox"><Checkbox disabled={!id} checked={!!id && selectedIds.has(id)} onChange={() => toggleOne(id)} /></TableCell>
                    <TableCell><Box component="span" sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 34, height: 28, px: 1, borderRadius: 999, color: "rgba(25,118,210,0.95)", backgroundColor: "rgba(25,118,210,0.10)", border: "1px solid rgba(25,118,210,0.20)", fontWeight: 900 }}>{page * rowsPerPage + idx + 1}</Box></TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>{row.AttendanceDate}</TableCell>
                    <TableCell>{row.HostelBlock || "—"}</TableCell><TableCell>{row.Floor || "—"}</TableCell><TableCell>{row.RoomNumber || "—"}</TableCell>
                    <TableCell><Chip size="small" label={isPresent ? "Present" : "Absent"} color={isPresent ? "success" : "error"} variant={isPresent ? "outlined" : "filled"} sx={{ fontWeight: 800 }} /></TableCell>
                  </TableRow>;
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={dateRows.length} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value,10)); setPage(0); }} rowsPerPageOptions={[10,20,30,50,100]} />
          {deletableIds.length === 0 && <Typography variant="caption" color="text.secondary">Present sessions have no hostelattendance row to delete. Only absent exception rows can be selected.</Typography>}
        </>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900, color: THEME_ORANGE }}>
          Delete Absent Attendance Record{selectedIds.size === 1 ? "" : "s"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, color: "rgba(0,0,0,0.72)" }}>
            You are deleting {selectedIds.size} absent attendance record{selectedIds.size === 1 ? "" : "s"}. A remark is mandatory and will be stored in the deletion audit log.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Deletion Remark *"
            value={deleteRemark}
            onChange={(e) => {
              setDeleteRemark(e.target.value);
              if (e.target.value.trim()) setDeleteRemarkError("");
            }}
            error={Boolean(deleteRemarkError)}
            helperText={deleteRemarkError || "Explain why this attendance absence record is being removed."}
            inputProps={{ maxLength: 1000 }}
            sx={{
              "& .MuiInputLabel-root": { color: "rgba(0,0,0,0.72)", fontWeight: 600 },
              "& .MuiInputLabel-root.Mui-focused": { color: "rgba(0,0,0,0.72)" },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting} sx={{ textTransform: "none", fontWeight: 800 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={deleteSelected}
            disabled={deleting || !deleteRemark.trim()}
            sx={{ textTransform: "none", fontWeight: 800 }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const getDefaultDateRange = () => {
  const today = new Date();
  const from = new Date(today);
  from.setDate(today.getDate() - 7);
  const fmt = (d) => d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  return { fromDate: fmt(from), toDate: fmt(today) };
};

export default function UpdateHostelAttendanceReport() {
  const token = localStorage.getItem("auth");

  // Theme constants
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240, 120, 40, 0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG, fontFamily: FONT_FAMILY };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
    backgroundColor: "transparent",
    fontFamily: FONT_FAMILY,
    "& .MuiTypography-root, & .MuiButton-root, & .MuiChip-root, & .MuiTableCell-root, & .MuiInputBase-root, & .MuiInputLabel-root": { fontFamily: FONT_FAMILY },
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "#fff",
    border: `1px solid rgba(25,118,210,0.20)`,
  };

  const sectionSx = { ...surfaceSx, p: 2 };

  const inputSx = {
    fontFamily: FONT_FAMILY,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    "& .MuiInputLabel-root": {
      fontFamily: FONT_FAMILY,
      fontWeight: 400,
    },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
    "& .MuiInputBase-input": { fontFamily: FONT_FAMILY, color: "rgba(0,0,0,0.78)" },
  };

  const primaryBtnSx = {
    fontFamily: FONT_FAMILY,
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
    px: 3,
    py: 0.85,
  };

  const outlinedBtnSx = {
    fontFamily: FONT_FAMILY,
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 800,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
    px: 2.5,
    py: 0.85,
  };

  const tableCardSx = {
    ...surfaceSx,
    mt: 2.5,
    p: 0,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      fontFamily: FONT_FAMILY,
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1.25,
      fontSize: "0.875rem",
    },
  };

  const subTheadRowSx = {
    backgroundColor: "rgba(25, 118, 210, 0.90)",
    "& th, & .MuiTableCell-root": {
      fontFamily: FONT_FAMILY,
      color: "#ffffff !important",
      fontWeight: 800,
      borderBottom: "1px solid rgba(255, 255, 255, 0.20)",
      whiteSpace: "nowrap",
      py: 1,
      fontSize: "0.82rem",
    },
  };

  const chipSx = {
    fontFamily: FONT_FAMILY,
    borderRadius: 2,
    backgroundColor: "rgba(25,118,210,0.06)",
    border: "1px solid rgba(25,118,210,0.18)",
    fontWeight: 800,
  };

  const autoCompleteSx = {
    ...inputSx,
    "& .MuiAutocomplete-tag": {
      fontFamily: FONT_FAMILY,
      borderRadius: 8,
      backgroundColor: "rgba(25,118,210,0.06)",
      border: "1px solid rgba(25,118,210,0.18)",
      fontWeight: 800,
    },
    "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": {
      color: "rgba(25,118,210,0.75)",
      "&:hover": { backgroundColor: "rgba(25,118,210,0.06)" },
    },
  };

  // State
  const [campusCode, setCampusCode] = useState("");
  const [filters, setFilters] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    SemesterCode: [],
    BranchCode: [],
    HostelBlock: [],
    Floor: [],
    RoomNumber: [],
    StudentID: [],
    FromDate: getDefaultDateRange().fromDate,
    ToDate: getDefaultDateRange().toDate,
  });
  const [studentIdInput, setStudentIdInput] = useState("");

  const [options, setOptions] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    SemesterCode: [],
    BranchCode: [],
    HostelBlock: [],
    Floor: [],
    RoomNumber: [],
  });

  const [loadingLookups, setLoadingLookups] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [roomRows, setRoomRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [openRoomKeys, setOpenRoomKeys] = useState({});
  const [openStudentKeys, setOpenStudentKeys] = useState({});

  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });

  useEffect(() => {
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(payload.campusCode || payload.CampusCode || "");
    } catch (e) {
      console.error("Failed to decode token", e);
      setToast({ open: true, type: "error", msg: "Invalid session token." });
    }
  }, [token]);

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  useEffect(() => {
    if (!campusCode) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingLookups(true);
        const { data } = await axios.get(`${API_URL}/api/HostelAttendance/lookups`, {
          ...authHeader,
          params: { CampusCode: campusCode },
        });
        if (cancelled) return;
        setOptions((prev) => ({
          SubCampusCode: Array.isArray(data?.SubCampusCode) ? data.SubCampusCode : (prev?.SubCampusCode || []),
          DegreeCode: Array.isArray(data?.DegreeCode) ? data.DegreeCode : (prev?.DegreeCode || []),
          SemesterCode: Array.isArray(data?.SemesterCode) ? data.SemesterCode.map(String) : (prev?.SemesterCode || []),
          BranchCode: Array.isArray(data?.BranchCode) ? data.BranchCode : (prev?.BranchCode || []),
          HostelBlock: Array.isArray(data?.HostelBlock) ? data.HostelBlock : (prev?.HostelBlock || []),
          Floor: Array.isArray(data?.Floor) ? data.Floor.map(String) : (prev?.Floor || []),
          RoomNumber: Array.isArray(data?.RoomNumber) ? data.RoomNumber.map(String) : (prev?.RoomNumber || []),
        }));
      } catch (e) {
        console.error(e);
        setToast({ open: true, type: "error", msg: "Failed to load dropdowns." });
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [campusCode, authHeader]);

  const parseIDs = (txt) =>
    Array.from(
      new Set(
        (txt || "")
          .split(/[\s,]+/g)
          .map((s) => s.trim())
          .filter(Boolean)
      )
    );

  const hasAnyFilter = () =>
    Boolean(campusCode) ||
    (filters.StudentID?.length ?? 0) > 0 ||
    (filters.SubCampusCode?.length ?? 0) > 0 ||
    (filters.HostelBlock?.length ?? 0) > 0 ||
    (filters.DegreeCode?.length ?? 0) > 0 ||
    (filters.BranchCode?.length ?? 0) > 0 ||
    (filters.SemesterCode?.length ?? 0) > 0 ||
    (filters.Floor?.length ?? 0) > 0 ||
    (filters.RoomNumber?.length ?? 0) > 0 ||
    Boolean(filters.FromDate) || Boolean(filters.ToDate);

  const buildParams = () => {
    const p = { CampusCode: campusCode };
    if (filters.FromDate) p.FromDate = filters.FromDate;
    if (filters.ToDate) p.ToDate = filters.ToDate;
    if (filters.SubCampusCode?.length) p.SubCampusCode = filters.SubCampusCode.join(",");
    if (filters.DegreeCode?.length) p.DegreeCode = filters.DegreeCode.join(",");
    if (filters.SemesterCode?.length) p.SemesterCode = filters.SemesterCode.join(",");
    if (filters.BranchCode?.length) p.BranchCode = filters.BranchCode.join(",");
    if (filters.HostelBlock?.length) p.HostelBlock = filters.HostelBlock.join(",");
    if (filters.Floor?.length) p.Floor = filters.Floor.join(",");
    if (filters.RoomNumber?.length) p.RoomNumber = filters.RoomNumber.join(",");
    if (filters.StudentID?.length) p.StudentID = filters.StudentID.join(",");
    return p;
  };

  const fetchSummary = async () => {
    if (!hasAnyFilter()) {
      return setToast({
        open: true,
        type: "error",
        msg: "Select at least one filter or Student ID.",
      });
    }
    setLoadingSummary(true);
    setRoomRows([]);
    setOpenRoomKeys({});
    setOpenStudentKeys({});

    try {
      const { data } = await axios.get(`${API_URL}/api/HostelAttendance/summary`, {
        ...authHeader,
        params: buildParams(),
      });

      const rawRows = Array.isArray(data) ? data : [];

      // Group student records into Rooms
      const roomMap = new Map();
      rawRows.forEach((r) => {
        // Enforce block filter guard
        const block = (r.HostelBlock || "").trim();
        if (filters.HostelBlock?.length > 0 && block && block !== "—" && !filters.HostelBlock.includes(block)) {
          return;
        }

        // Enforce subcampus filter guard
        const subCampus = (r.SubCampusCode || "").trim();
        if (filters.SubCampusCode?.length > 0 && subCampus && subCampus !== "—" && !filters.SubCampusCode.includes(subCampus)) {
          return;
        }

        // Only enforce student filters if record has a StudentID
        if (r.StudentID) {
          // Enforce degree filter guard
          const degree = (r.DegreeCode || "").trim();
          if (filters.DegreeCode?.length > 0 && !filters.DegreeCode.includes(degree)) {
            return;
          }

          // Enforce branch filter guard
          const branch = (r.BranchCode || "").trim();
          if (filters.BranchCode?.length > 0 && !filters.BranchCode.includes(branch)) {
            return;
          }

          // Enforce semester filter guard
          const sem = String(r.Semester || "").trim();
          if (filters.SemesterCode?.length > 0 && !filters.SemesterCode.map(String).includes(sem)) {
            return;
          }

          // Enforce student ID filter guard
          const sId = (r.StudentID || "").trim();
          if (filters.StudentID?.length > 0 && !filters.StudentID.includes(sId)) {
            return;
          }
        }

        const roomKey = `${r.HostelBlock || ""}|${r.Floor || ""}|${r.RoomNumber || ""}`;
        if (!roomMap.has(roomKey)) {
          roomMap.set(roomKey, {
            roomKey,
            block: r.HostelBlock || "—",
            floor: r.Floor || "—",
            room: r.RoomNumber || "—",
            students: [],
            totalStudents: 0,
            presentStudents: 0,
            absentStudents: 0,
          });
        }

        if (r.StudentID) {
          const rm = roomMap.get(roomKey);
          rm.students.push(r);
          rm.totalStudents += 1;

          rm.presentStudents += Number(r.PresentDays || 0);
          rm.absentStudents += Number(r.AbsentDays || 0);
        }
      });

      const aggregatedRooms = Array.from(roomMap.values());
      setRoomRows(aggregatedRooms);
      setPage(0);
    } catch (err) {
      console.error(err);
      setToast({ open: true, type: "error", msg: "Failed to fetch hostel attendance summary." });
    } finally {
      setLoadingSummary(false);
    }
  };

  const toggleRoom = (roomKey) => {
    setOpenRoomKeys((p) => ({ ...p, [roomKey]: !p[roomKey] }));
  };

  const toggleStudent = (studentKey) => {
    setOpenStudentKeys((p) => ({ ...p, [studentKey]: !p[studentKey] }));
  };

  const zebraRowSx = (i) => ({
    backgroundColor: i % 2 === 0 ? ROW_ALT : "#FFFFFF",
    cursor: "pointer",
    transition: "background-color 0.15s ease",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": {
      fontFamily: FONT_FAMILY,
      borderBottom: "1px solid rgba(25,118,210,0.10)",
      py: 1.25,
      fontSize: "0.875rem",
    },
  });

  const subZebraRowSx = (i) => ({
    backgroundColor: i % 2 === 0 ? "rgba(240, 120, 40, 0.02)" : "#FFFFFF",
    cursor: "pointer",
    transition: "background-color 0.15s ease",
    "&:hover": { backgroundColor: "rgba(240, 120, 40, 0.08)" },
    "& td": {
      fontFamily: FONT_FAMILY,
      borderBottom: "1px solid rgba(240,120,40,0.12)",
      py: 1,
      fontSize: "0.84rem",
    },
  });

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* Header */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: "1.15rem", fontWeight: 900, lineHeight: 1.2 }}>
              Update Hostel Attendance Report
            </Typography>
            <Typography sx={{ fontFamily: FONT_FAMILY, opacity: 0.9, fontSize: "0.85rem", mt: 0.3 }}>
              Review hostel attendance by room and delete absent exception records from the expanded Attendance Dates Log
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* Filters section */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Filters
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              {/* Academic filters: 4 filters in one row */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                  mb: 1.5,
                }}
              >
                <Autocomplete multiple
                  options={Array.isArray(options.SubCampusCode) ? options.SubCampusCode : []}
                  value={Array.isArray(filters.SubCampusCode) ? filters.SubCampusCode : []}
                  onChange={(_, value) => setFilters((p) => ({ ...p, SubCampusCode: value || [] }))}
                  disableCloseOnSelect filterSelectedOptions loading={loadingLookups} sx={autoCompleteSx}
                  renderInput={(params) => <TextField {...params} label="SubCampusCode(s)" size="small" sx={inputSx} />}
                />

                <Autocomplete multiple
                  options={Array.isArray(options.DegreeCode) ? options.DegreeCode : []}
                  value={Array.isArray(filters.DegreeCode) ? filters.DegreeCode : []}
                  onChange={(_, value) => setFilters((p) => ({ ...p, DegreeCode: value || [] }))}
                  disableCloseOnSelect filterSelectedOptions loading={loadingLookups} sx={autoCompleteSx}
                  renderInput={(params) => <TextField {...params} label="DegreeCode(s)" size="small" sx={inputSx} />}
                />

                <Autocomplete multiple
                  options={Array.isArray(options.BranchCode) ? options.BranchCode : []}
                  value={Array.isArray(filters.BranchCode) ? filters.BranchCode : []}
                  onChange={(_, value) => setFilters((p) => ({ ...p, BranchCode: value || [] }))}
                  disableCloseOnSelect filterSelectedOptions loading={loadingLookups} sx={autoCompleteSx}
                  renderInput={(params) => <TextField {...params} label="BranchCode(s)" size="small" sx={inputSx} />}
                />

                <Autocomplete multiple
                  options={Array.isArray(options.SemesterCode) ? options.SemesterCode : []}
                  value={Array.isArray(filters.SemesterCode) ? filters.SemesterCode : []}
                  onChange={(_, value) => setFilters((p) => ({ ...p, SemesterCode: (value || []).map(String) }))}
                  disableCloseOnSelect filterSelectedOptions loading={loadingLookups} sx={autoCompleteSx}
                  renderInput={(params) => <TextField {...params} label="Semester(s)" size="small" sx={inputSx} />}
                />
              </Box>

              {/* Hostel/date filters: 5 filters in one row */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "repeat(5, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Autocomplete multiple
                  options={Array.isArray(options.HostelBlock) ? options.HostelBlock : []}
                  value={Array.isArray(filters.HostelBlock) ? filters.HostelBlock : []}
                  onChange={(_, value) => setFilters((p) => ({ ...p, HostelBlock: value || [] }))}
                  disableCloseOnSelect filterSelectedOptions loading={loadingLookups} sx={autoCompleteSx}
                  renderInput={(params) => <TextField {...params} label="Hostel Block(s)" size="small" sx={inputSx} />}
                />

                <Autocomplete multiple
                  options={Array.isArray(options.Floor) ? options.Floor : []}
                  value={Array.isArray(filters.Floor) ? filters.Floor : []}
                  onChange={(_, value) => setFilters((p) => ({ ...p, Floor: (value || []).map(String) }))}
                  disableCloseOnSelect filterSelectedOptions loading={loadingLookups} sx={autoCompleteSx}
                  renderInput={(params) => <TextField {...params} label="Floor(s)" size="small" sx={inputSx} />}
                />

                <Autocomplete multiple
                  options={Array.isArray(options.RoomNumber) ? options.RoomNumber : []}
                  value={Array.isArray(filters.RoomNumber) ? filters.RoomNumber : []}
                  onChange={(_, value) => setFilters((p) => ({ ...p, RoomNumber: (value || []).map(String) }))}
                  disableCloseOnSelect filterSelectedOptions loading={loadingLookups} sx={autoCompleteSx}
                  renderInput={(params) => <TextField {...params} label="Room(s)" size="small" sx={inputSx} />}
                />

                <TextField fullWidth size="small" type="date" label="From Date"
                  InputLabelProps={{ shrink: true }} value={filters.FromDate || ""}
                  onChange={(e) => setFilters((p) => ({ ...p, FromDate: e.target.value }))} sx={inputSx}
                />

                <TextField fullWidth size="small" type="date" label="To Date"
                  InputLabelProps={{ shrink: true }} value={filters.ToDate || ""}
                  onChange={(e) => setFilters((p) => ({ ...p, ToDate: e.target.value }))} sx={inputSx}
                />
              </Box>

              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 1, alignItems: "flex-start" }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Student IDs (comma / space / newline)"
                    placeholder="e.g. STUID01, STUID02"
                    value={studentIdInput}
                    onChange={(e) => {
                      const txt = e.target.value;
                      setStudentIdInput(txt);
                      const ids = parseIDs(txt);
                      setFilters((p) => ({ ...p, StudentID: ids }));
                    }}
                    sx={inputSx}
                  />

                  {filters.StudentID.length > 0 && (
                    <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                      {filters.StudentID.map((id) => (
                        <Chip
                          key={id}
                          size="small"
                          label={id}
                          onDelete={() => {
                            const next = filters.StudentID.filter((x) => x !== id);
                            setFilters((p) => ({ ...p, StudentID: next }));
                            setStudentIdInput(next.join(", "));
                          }}
                          sx={{ ...chipSx, mb: 1 }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", pt: { xs: 1, md: 0 } }}>
                  <Button
                    variant="contained"
                    sx={primaryBtnSx}
                    onClick={fetchSummary}
                    disabled={loadingSummary}
                  >
                    {loadingSummary ? "Loading…" : "Search"}
                  </Button>

                  <Button
                    variant="outlined"
                    sx={outlinedBtnSx}
                    onClick={() => {
                      setFilters({
                        SubCampusCode: [],
                        DegreeCode: [],
                        SemesterCode: [],
                        BranchCode: [],
                        HostelBlock: [],
                        Floor: [],
                        RoomNumber: [],
                        StudentID: [],
                        FromDate: getDefaultDateRange().fromDate,
                        ToDate: getDefaultDateRange().toDate,
                      });
                      setStudentIdInput("");
                      setRoomRows([]);
                      setOpenRoomKeys({});
                      setOpenStudentKeys({});
                    }}
                  >
                    Clear Filters
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Paper>

          {/* LEVEL 1: ROOMS SUMMARY TABLE */}
          <Paper elevation={0} sx={tableCardSx}>
            <Box sx={{ overflowX: "auto", overflowY: "hidden", borderRadius: 2 }}>
              <TableContainer component="div">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={theadRowSx}>
                      <TableCell sx={{ width: 48, textAlign: "center" }}></TableCell>
                      <TableCell>Block</TableCell>
                      <TableCell>Floor</TableCell>
                      <TableCell>Room</TableCell>
                      <TableCell align="center">Total Students</TableCell>
                      <TableCell align="center">Present</TableCell>
                      <TableCell align="center">Absent</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingSummary ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 3, justifyContent: "center" }}>
                            <CircularProgress size={24} sx={{ color: THEME_ORANGE }} />
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: "text.secondary" }}>
                              Loading hostel room summary…
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : roomRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography sx={{ p: 3, textAlign: "center", color: "text.secondary", fontFamily: FONT_FAMILY }}>
                            {hasAnyFilter()
                              ? "No data found for selected filters."
                              : "Select filters and click Search to view hostel attendance."}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      roomRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((rm, i) => {
                        const isRoomOpen = !!openRoomKeys[rm.roomKey];

                        return (
                          <React.Fragment key={rm.roomKey || i}>
                            {/* LEVEL 1 ROW: ROOM */}
                            <TableRow sx={zebraRowSx(i)} onClick={() => toggleRoom(rm.roomKey)}>
                              <TableCell sx={{ width: 48, textAlign: "center", py: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleRoom(rm.roomKey);
                                  }}
                                  sx={{ color: THEME_ORANGE }}
                                >
                                  {isRoomOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                </IconButton>
                              </TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>{rm.block}</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>{rm.floor}</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>
                                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                                  <MeetingRoomIcon sx={{ fontSize: "1rem", color: THEME_ORANGE }} />
                                  <span>{rm.room}</span>
                                </Box>
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 800, color: "#1e293b" }}>
                                {rm.totalStudents}
                              </TableCell>
                              <TableCell align="center" sx={{ color: "success.main", fontWeight: 800 }}>
                                {rm.presentStudents}
                              </TableCell>
                              <TableCell align="center" sx={{ color: "error.main", fontWeight: 800 }}>
                                {rm.absentStudents}
                              </TableCell>
                            </TableRow>

                            {/* LEVEL 2 EXPANSION: STUDENTS IN THIS ROOM */}
                            <TableRow>
                              <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                                <Collapse in={isRoomOpen} timeout="auto" unmountOnExit>
                                  <Box
                                    sx={{
                                      m: 2,
                                      p: 2,
                                      borderRadius: 2,
                                      backgroundColor: "#FAFAFA",
                                      border: "1px solid rgba(240, 120, 40, 0.25)",
                                    }}
                                  >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                                      <PersonIcon sx={{ color: THEME_ORANGE, fontSize: "1.2rem" }} />
                                      <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 900, fontSize: "0.95rem", color: THEME_ORANGE }}>
                                        Students in Room {rm.room} (Block: {rm.block}, Subcampus: {rm.subCampus})
                                      </Typography>
                                      <Chip
                                        size="small"
                                        label={`${rm.students.length} Student${rm.students.length === 1 ? "" : "s"}`}
                                        sx={{
                                          fontFamily: FONT_FAMILY,
                                          fontWeight: 700,
                                          fontSize: "0.75rem",
                                          backgroundColor: "rgba(25,118,210,0.08)",
                                          ml: 1,
                                        }}
                                      />
                                    </Box>

                                    <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: 1.5 }}>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={subTheadRowSx}>
                                            <TableCell sx={{ width: 44, textAlign: "center", color: "#ffffff !important" }}></TableCell>
                                            <TableCell sx={{ color: "#ffffff !important", fontWeight: 800 }}>Student Name</TableCell>
                                            <TableCell sx={{ color: "#ffffff !important", fontWeight: 800 }}>Student Roll No</TableCell>
                                            <TableCell align="center" sx={{ color: "#ffffff !important", fontWeight: 800 }}>Total Days</TableCell>
                                            <TableCell align="center" sx={{ color: "#ffffff !important", fontWeight: 800 }}>Total Present</TableCell>
                                            <TableCell align="center" sx={{ color: "#ffffff !important", fontWeight: 800 }}>Total Absent Days</TableCell>
                                            <TableCell align="center" sx={{ color: "#ffffff !important", fontWeight: 800 }}>Percentage</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {rm.students.length === 0 ? (
                                            <TableRow>
                                              <TableCell
                                                colSpan={7}
                                                sx={{
                                                  textAlign: "center",
                                                  py: 2.5,
                                                  color: "text.secondary",
                                                  fontFamily: FONT_FAMILY,
                                                  fontSize: "0.85rem",
                                                  fontStyle: "italic",
                                                }}
                                              >
                                                No student records found in this room.
                                              </TableCell>
                                            </TableRow>
                                          ) : (
                                            rm.students.map((s, sIdx) => {
                                              const studentKey = `${s.StudentID}|${rm.block}|${rm.room}`;
                                              const isStudentOpen = !!openStudentKeys[studentKey];

                                              return (
                                                <React.Fragment key={studentKey || sIdx}>
                                                  {/* LEVEL 2 ROW: STUDENT */}
                                                  <TableRow
                                                    sx={subZebraRowSx(sIdx)}
                                                    onClick={() => toggleStudent(studentKey)}
                                                  >
                                                    <TableCell sx={{ width: 44, textAlign: "center", py: 0.8 }}>
                                                      <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          toggleStudent(studentKey);
                                                        }}
                                                        sx={{ color: THEME_ORANGE }}
                                                      >
                                                        {isStudentOpen ? (
                                                          <KeyboardArrowDownIcon fontSize="small" />
                                                        ) : (
                                                          <KeyboardArrowRightIcon fontSize="small" />
                                                        )}
                                                      </IconButton>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: "800 !important", color: "#0f172a" }}>
                                                      {s.StudentName}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 800, color: "rgba(25,118,210,0.95)" }}>
                                                      {s.StudentID}
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                                                      {s.TotalDays}
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ color: "success.main", fontWeight: 800 }}>
                                                      {s.PresentDays}
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ color: "error.main", fontWeight: 800 }}>
                                                      {s.AbsentDays}
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 800, color: THEME_ORANGE }}>
                                                      {s.AttendancePercentage}%
                                                    </TableCell>
                                                  </TableRow>

                                                  {/* LEVEL 3 EXPANSION: DATES LOG WITH PAGINATION */}
                                                  <TableRow>
                                                    <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                                                      <Collapse in={isStudentOpen} timeout="auto" unmountOnExit>
                                                        <StudentAttendanceDatesHistory
                                                          studentId={s.StudentID}
                                                          studentName={s.StudentName}
                                                          campusCode={campusCode}
                                                          authHeader={authHeader}
                                                          THEME_ORANGE={THEME_ORANGE}
                                                          fromDate={filters.FromDate}
                                                          toDate={filters.ToDate}
                                                        />
                                                      </Collapse>
                                                    </TableCell>
                                                  </TableRow>
                                                </React.Fragment>
                                              );
                                            })
                                          )}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>

          <TablePagination
            component="div"
            count={roomRows.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100, 200, 300]}
            showFirstButton
            showLastButton
            sx={{ ...surfaceSx, mt: 1, fontFamily: FONT_FAMILY }}
          />

          {/* Toast Notification */}
          <Snackbar
            open={toast.open}
            autoHideDuration={3000}
            onClose={() => setToast((p) => ({ ...p, open: false }))}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity={toast.type === "error" ? "error" : toast.type === "info" ? "info" : "success"}
              sx={{ borderRadius: 2, fontFamily: FONT_FAMILY }}
              onClose={() => setToast((p) => ({ ...p, open: false }))}
            >
              {toast.msg}
            </Alert>
          </Snackbar>
        </Box>
      </Paper>
    </Box>
  );
}
